import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

@customElement('umb-market-package-version-badges')
export class UmbMarketPackageVersionBadges extends LitElement {
  @property({ type: Array })
  versionsSupported: Array<number> = [];

  @property({ type: Boolean })
  cloudCertified = false;

  readonly #em = '—';

  #renderVersions() {
    // if we have five or more consecutive versions, drop the middle three
    const versions = this.#replaceConsecutiveWithDash(
      this.versionsSupported
    ).map(x => (x === this.#em ? x : `v${x}`));

    if (this.cloudCertified) {
      versions.push('Cloud');
    }

    if (versions.length === 0) {
      versions.push('?');
    }

    return html`
      <ul id="versions">
        ${versions.map(
          v =>
            html`<li class="version ${v === this.#em ? 'range' : ''}">${v}</li>`
        )}
      </ul>
    `;
  }

  #replaceConsecutiveWithDash(arr: Array<number>) {
    // short, show everything
    if (arr.length < 4) return arr;

    let i = 0;
    const result: Array<string | number> = [];

    while (i < arr.length) {
      // Check if there's a sequence of at least 4 consecutive numbers starting at index i
      if (i <= arr.length - 4 && arr[i + 3] === arr[i] + 3) {
        // Find the end of this consecutive sequence
        let end = i + 3;
        while (end < arr.length && arr[end + 1] === arr[end] + 1) {
          end += 1;
        }
        // Add the start, dash, and end of the sequence to the result
        result.push(arr[i], this.#em, arr[end]);
        // Skip the rest of the sequence
        i = end + 1;
      } else {
        // Not a part of a consecutive sequence, so just add the number
        result.push(arr[i]);
        i += 1;
      }
    }

    return result;
  }

  render() {
    if (!this.versionsSupported.length && !this.cloudCertified) {
      return null;
    }

    return html`${this.#renderVersions()}`;
  }

  static styles = [
    css`
      :host {
        --font-size: var(--badge-font-size, 12px);
        --font-weight: var(--badge-font-weight, 400);
        --fill: var(--color-border);
      }

      #versions {
        list-style-type: none;
        display: flex;
        margin: auto auto 0 0;
        padding: 0;
      }

      .version {
        border: 1px solid var(--color-border);
        box-sizing: border-box;
        font-size: var(--font-size);
        font-weight: var(--font-weight);
        height: var(--badge-height, var(--l-spacer));
        display: flex;
        align-items: center;
      }

      .version {
        line-height: normal;
        padding: 0 8px;
      }

      .version:first-child {
        padding-left: 14px;
        border-radius: var(--s-spacer) 0 0 var(--s-spacer);
      }

      .version:last-child {
        padding-right: 14px;
        border-radius: 0 var(--s-spacer) var(--s-spacer) 0;
      }

      .version:only-child {
        border-radius: var(--s-spacer);
      }

      .version + .version {
        border-left: none;
      }

      /* lit-plugin will mark as an error, this is valid css to select the preceding sibling */
      .version:has(+ .range) {
        border-right: none;
      }

      .range {
        padding: 0;
        border-right: none;
      }
    `,
  ];
}
